import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { LANGS, useLang } from './i18n';

// Prepínač jazyka — odkazy na jazykové cesty (/, /cz, /en) kvôli SEO.
// variant="inline"  → všetky jazyky viditeľné naraz (mobil)
// variant="popover" → 🌐 + zvolený jazyk, po kliknutí dropdown (desktop)
const LanguageSwitcher = ({ className = '', variant = 'inline' }) => {
  const lang = useLang();

  if (variant === 'popover') {
    return <LanguagePopover className={className} lang={lang} />;
  }

  return (
    <div className={`landing-langswitch ${className}`.trim()} role="group" aria-label="Language">
      <Globe size={15} className="landing-langswitch__icon" aria-hidden />
      {LANGS.map((l) => (
        <Link
          key={l.code}
          to={l.path}
          className={`landing-langswitch__opt${l.code === lang ? ' is-active' : ''}`}
          aria-current={l.code === lang ? 'true' : undefined}
          hrefLang={l.htmlLang}
        >
          {l.short}
        </Link>
      ))}
    </div>
  );
};

const LanguagePopover = ({ className, lang }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`landing-langpop ${className}`.trim()}>
      <button
        type="button"
        className={`landing-langpop__trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Language"
      >
        <Globe size={15} className="landing-langpop__globe" aria-hidden />
        <span className="landing-langpop__code">{current.short}</span>
        <ChevronDown size={14} className="landing-langpop__chev" aria-hidden />
      </button>

      {open && (
        <div className="landing-langpop__menu" role="menu" aria-label="Language">
          {LANGS.map((l) => {
            const isActive = l.code === lang;
            return (
              <Link
                key={l.code}
                to={l.path}
                role="menuitem"
                className={`landing-langpop__item${isActive ? ' is-active' : ''}`}
                aria-current={isActive ? 'true' : undefined}
                hrefLang={l.htmlLang}
                onClick={() => setOpen(false)}
              >
                <span className="landing-langpop__item-code">{l.short}</span>
                <span className="landing-langpop__item-label">{l.label}</span>
                {isActive && <Check size={15} className="landing-langpop__check" aria-hidden />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
